import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Slider,
} from '@mui/material';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareIcon from '@mui/icons-material/Share';
import IosShareIcon from '@mui/icons-material/IosShare';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { getItem, deleteItem, refreshItem, updateItemFlags } from '../services/items';
import { getDocumentsForItem, getDocumentFolders, updateDocument } from '../services/documents';
import type { StashDocument, DocumentFolder } from '../types';
import { getUsersByIds, type UserInfo } from '../services/users';
import { useAuth } from '../contexts/AuthContext';
import type { Item } from '../types';
import { ItemFlags, hasFlag } from '../types';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';
import CommentSection from '../components/items/CommentSection';
import EditItemDialog from '../components/items/EditItemDialog';
import DebugDialog from '../components/items/DebugDialog';
import ShareDialog from '../components/sharing/ShareDialog';
import MoveDocumentDialog from '../components/documents/MoveDocumentDialog';

export default function ItemDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [articleExpanded, setArticleExpanded] = useState(false);
  const [articleOverflows, setArticleOverflows] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);
  const [creator, setCreator] = useState<UserInfo | null>(null);
  const [attachments, setAttachments] = useState<StashDocument[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [moveAttachment, setMoveAttachment] = useState<StashDocument | null>(null);
  const [fontScale, setFontScale] = useState<number>(() => {
    const saved = Number(localStorage.getItem('stash-body-font-scale'));
    return saved >= 0.7 && saved <= 1.4 ? saved : 0.95;
  });

  const bodyFontSize = `${fontScale}rem`;
  const bodyLineHeight = 1.5 + (fontScale - 0.7) * 0.43; // ~1.5 at 0.7, ~1.8 at 1.4

  const loadItem = useCallback(() => {
    if (!itemId) return;
    getItem(itemId).then((result) => {
      setItem(result);
      setLoading(false);
      if (result?.createdBy) {
        getUsersByIds([result.createdBy]).then((users) => {
          if (users.length > 0) setCreator(users[0]);
        });
      }
    });
  }, [itemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  useEffect(() => {
    if (item?.type === 'email' && itemId) {
      getDocumentsForItem(itemId).then(setAttachments);
      getDocumentFolders().then(setFolders);
    }
  }, [item?.type, itemId]);

  // Check if article body overflows the collapsed height
  useEffect(() => {
    if (articleRef.current) {
      setArticleOverflows(articleRef.current.scrollHeight > 200);
    }
  }, [item?.articleBody]);

  // Poll for fresh data while enrichment is not yet complete
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (item?.enrichmentStatus === 'pending') {
      pollCountRef.current = 10; // start polling when we see a pending item
    }
  }, [item?.enrichmentStatus]);

  useEffect(() => {
    if (!itemId) return;
    const interval = setInterval(async () => {
      if (pollCountRef.current > 0) {
        pollCountRef.current--;
        const updated = await getItem(itemId);
        if (updated) setItem(updated);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [itemId]);

  // Auto-mark as read when item loads
  useEffect(() => {
    if (item && !hasFlag(item.flags, ItemFlags.READ)) {
      const newFlags = item.flags | ItemFlags.READ;
      setItem({ ...item, flags: newFlags });
      updateItemFlags(item.id, newFlags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return <Typography>Item not found.</Typography>;
  }

  const allTags = [...item.tags, ...item.autoTags];
  const isOwner = item.createdBy === user?.uid;

  const handleDelete = async () => {
    if (!itemId) return;
    await deleteItem(itemId);
    navigate('/');
  };

  const handleRefresh = async () => {
    if (!itemId || !item) return;
    setRefreshing(true);
    try {
      await refreshItem(itemId);
      // Immediately mark as pending so polling starts and UI updates
      setItem({ ...item, enrichmentStatus: 'pending' });
    } catch {
      // still reload to get current state
      loadItem();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {item.title}
        </Typography>
      </Box>
      {isOwner && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
            <IconButton size="small" onClick={() => {
              if (!item) return;
              const newFlags = hasFlag(item.flags, ItemFlags.STARRED)
                ? item.flags & ~ItemFlags.STARRED
                : item.flags | ItemFlags.STARRED;
              setItem({ ...item, flags: newFlags });
              updateItemFlags(item.id, newFlags);
            }} sx={{ color: hasFlag(item.flags, ItemFlags.STARRED) ? 'warning.main' : undefined }}>
              {hasFlag(item.flags, ItemFlags.STARRED) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => {
              if (!item) return;
              const newFlags = hasFlag(item.flags, ItemFlags.READ)
                ? item.flags & ~ItemFlags.READ
                : item.flags | ItemFlags.READ;
              setItem({ ...item, flags: newFlags });
              updateItemFlags(item.id, newFlags);
            }}>
              {hasFlag(item.flags, ItemFlags.READ)
                ? <MarkEmailReadIcon fontSize="small" />
                : <MarkEmailUnreadIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setDebugDialogOpen(true)}>
              <BugReportIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setEditDialogOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={async () => {
              const shareUrl = `${window.location.origin}/items/${itemId}`;
              if (navigator.share) {
                try { await navigator.share({ title: item.title, text: item.summary || item.title, url: shareUrl }); } catch { /* cancelled */ }
              } else {
                await navigator.clipboard.writeText(shareUrl);
              }
            }}>
              <IosShareIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setShareDialogOpen(true)}>
              <ShareIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={handleDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0, width: 130 }}>
              <TextDecreaseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Slider
                size="small"
                min={0.7}
                max={1.4}
                step={0.05}
                value={fontScale}
                onChange={(_e, val) => {
                  const v = val as number;
                  setFontScale(v);
                  localStorage.setItem('stash-body-font-scale', String(v));
                }}
                sx={{ mx: 0.5 }}
              />
              <TextIncreaseIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </Box>
        </Box>
      )}

      {item.enrichmentStatus === 'pending' && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Enriching — fetching metadata, generating summary...
          </Typography>
        </Box>
      )}

      {item.imageUrl && item.imageUrl.startsWith('http') && (
        <CardMedia
          component="img"
          image={item.imageUrl}
          alt={item.title}
          sx={{ borderRadius: 2, mb: 2, maxHeight: 400, objectFit: 'cover' }}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        {(item.type === 'url' || allTags.length > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: (item.type === 'text' || item.type === 'email' || item.type === 'file') ? 2 : 2 }}>
            {item.type === 'url' && (
              <Button
                href={item.content}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                variant="outlined"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                {(() => { try { return new URL(item.content).hostname.replace('www.', ''); } catch { return 'Open link'; } })()}
              </Button>
            )}
            {allTags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" onClick={() => navigate(`/?tag=${encodeURIComponent(tag)}`)} sx={{ cursor: 'pointer' }} />
            ))}
          </Box>
        )}

        {item.type === 'text' && (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontSize: bodyFontSize, lineHeight: bodyLineHeight }}>
            {item.content}
          </Typography>
        )}

        {item.type === 'email' && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>From:</strong> {item.emailFromName ? `${item.emailFromName} <${item.emailFrom}>` : item.emailFrom}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Subject:</strong> {item.title}
              </Typography>
            </Box>
            {attachments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Attachments</Typography>
                <List dense disablePadding>
                  {attachments.map((att) => (
                    <ListItem key={att.id} disablePadding sx={{ gap: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={att.name}
                        secondary={att.fileSize != null ? (att.fileSize < 1024 * 1024 ? `${(att.fileSize / 1024).toFixed(1)} KB` : `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`) : undefined}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <IconButton size="small" component="a" href={`${import.meta.env.VITE_API_URL || ''}/api/documents/${att.id}/download`} download>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {att.folderId == null && (
                        <IconButton size="small" onClick={() => setMoveAttachment(att)}>
                          <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {item.articleHtml ? (
              <Box
                component="iframe"
                srcDoc={item.articleHtml}
                sandbox="allow-same-origin"
                sx={{ width: '100%', border: 0, minHeight: 300, display: 'block' }}
                onLoad={(e: React.SyntheticEvent<HTMLIFrameElement>) => {
                  const iframe = e.currentTarget;
                  if (iframe.contentDocument?.body) {
                    iframe.style.height = iframe.contentDocument.body.scrollHeight + 32 + 'px';
                  }
                }}
              />
            ) : (
              <Box sx={{
                '& p': { mt: 0, mb: 1.5 },
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& a': { color: 'primary.main' },
                '& img': { maxWidth: '100%', borderRadius: 1 },
                '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
                '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto' },
                '& code': { fontSize: '0.875em' },
                fontSize: bodyFontSize,
                lineHeight: bodyLineHeight,
              }}>
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{item.articleBody || item.content || ''}</Markdown>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {item.summary && (
        <Paper variant="outlined" sx={{ mb: 2, borderColor: 'text.disabled', borderWidth: 1 }}>
          <Box sx={{ px: 2, py: 0.75, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="text.secondary">Summary</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ fontSize: bodyFontSize, lineHeight: bodyLineHeight }}>{item.summary}</Typography>
          </Box>
        </Paper>
      )}

      {item.articleBody && (
        <Paper variant="outlined" sx={{ mb: 2, borderColor: 'text.disabled', borderWidth: 1 }}>
          <Box sx={{ px: 2, py: 0.75, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="text.secondary">Article</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Box ref={articleRef} sx={{
              maxHeight: articleExpanded ? 'none' : 200,
              overflow: 'hidden',
            }}>
              <Box sx={{
                '& p': { mt: 0, mb: 1.5 },
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& a': { color: 'primary.main' },
                '& img': { maxWidth: '100%', borderRadius: 1 },
                '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
                '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto' },
                '& code': { fontSize: '0.875em' },
                fontSize: bodyFontSize,
                lineHeight: bodyLineHeight,
              }}>
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{item.articleBody}</Markdown>
              </Box>
            </Box>
            {(articleOverflows || articleExpanded) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Chip
                  label={articleExpanded ? 'Less' : 'More...'}
                  size="small"
                  onClick={() => setArticleExpanded((prev) => !prev)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {creator && (
          <>
            <Avatar src={creator.photoURL} sx={{ width: 24, height: 24 }} />
            <Typography variant="caption" color="text.secondary">
              {creator.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              &middot;
            </Typography>
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          Added {item.createdAt.toLocaleDateString()}
          {item.enrichmentStatus === 'pending' && ' — Enrichment pending...'}
        </Typography>
      </Box>

      {itemId && <CommentSection itemId={itemId} />}

      {itemId && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          title={item.title}
          itemId={itemId}
          currentSharedWith={item.sharedWith}
          onUpdated={loadItem}
        />
      )}

      {itemId && (
        <DebugDialog
          open={debugDialogOpen}
          onClose={() => setDebugDialogOpen(false)}
          itemId={itemId}
        />
      )}

      <EditItemDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        item={item}
        onUpdated={loadItem}
      />

      {moveAttachment && (
        <MoveDocumentDialog
          open
          folders={folders}
          currentFolderId={moveAttachment.folderId}
          onClose={() => setMoveAttachment(null)}
          onSave={async (folderId) => {
            await updateDocument(moveAttachment.id, { folderId });
            setMoveAttachment(null);
            if (itemId) getDocumentsForItem(itemId).then(setAttachments);
          }}
        />
      )}
    </Box>
  );
}
